import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import CandidateEditModal from './CandidateEditModal';
import PortalNavbar from './PortalNavbar';
import '../design-system.css';

const MetadataManager = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editId, setEditId] = useState(null);
    const [editData, setEditData] = useState({});
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    // New states for dynamic fields and filtering
    const [metadataFields, setMetadataFields] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({});
    const [showFilters, setShowFilters] = useState(false);

    // Pagination State
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        fetchMetadataFields();
    }, []);

    // Handle deep linking for open_cvid
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const openCvid = params.get('open_cvid');
        if (openCvid) {
            setSearchTerm(openCvid);
            // We need to wait for candidates to load, then find and open
            // But fetchCandidates depends on searchTerm, so setting it triggers fetch.
            // We just need to wait for the fetch to complete and then open the modal.
        }
    }, [location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const openCvid = params.get('open_cvid');
        // Check if we need to open the modal:
        // 1. We have an open_cvid param
        // 2. Candidates are loaded
        // 3. Modal is NOT currently open (to avoid loop if we were setting it to true, though handleEdit does that)
        // BUT: If we just close it, showModal becomes false, this effect runs, and re-opens it if param is still there.
        // FIX: Remove param after opening.

        if (openCvid && candidates.length > 0) {
            const candidate = candidates.find(c => String(c.cvid) === openCvid || String(c.cvid).includes(openCvid));
            if (candidate) {
                // Open the modal
                if (!showModal) {
                    handleEdit(candidate);
                }
                // Clear the param so it doesn't re-trigger or persist
                navigate(location.pathname, { replace: true });
            }
        }
    }, [candidates, location.search, showModal, navigate, location.pathname]);

    useEffect(() => {
        fetchCandidates();
    }, [page, limit, searchTerm, filters]);

    const fetchMetadataFields = async () => {
        try {
            const response = await axios.get('/api/metadata/fields');
            setMetadataFields(response.data);
        } catch (error) {
            console.error("Error fetching metadata fields", error);
        }
    };

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit,
                search: searchTerm,
                filters: JSON.stringify(filters)
            };
            const response = await axios.get('/api/candidates', { params });
            setCandidates(response.data.data);
            setTotalCount(response.data.total);
        } catch (error) {
            console.error("Error fetching candidates", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (cvid) => {
        if (!window.confirm(`Sei sicuro di voler eliminare il candidato ${cvid}?`)) return;
        try {
            await axios.delete(`/api/candidates/${cvid}`);
            setCandidates(candidates.filter(c => c.cvid !== cvid));
        } catch (error) {
            console.error("Error deleting candidate", error);
            alert("Errore durante l'eliminazione");
        }
    };

    const handleEdit = (candidate) => {
        setEditId(candidate.cvid);
        setEditData(candidate.metadata_json || {});
        setShowModal(true);
    };

    const handleSave = async (cvid, updatedData) => {
        try {
            await axios.put(`/api/candidates/${cvid}`, { metadata: updatedData });
            setEditId(null);
            setShowModal(false);
            fetchCandidates(); // Refresh to get updated data
        } catch (error) {
            console.error("Error updating candidate", error);
            alert("Errore durante l'aggiornamento");
        }
    };



    const handleMetadataChange = (key, value) => {
        setEditData({ ...editData, [key]: value });
    };

    const handleFilterChange = (key, value) => {
        setFilters({ ...filters, [key]: value });
    };

    // Filtering Logic removed (handled by backend)
    // const filteredCandidates = candidates...






    return (
        <div className="ds-page">
            <PortalNavbar section="Gestione Candidati" showLogout />
            <main className="ds-page-main">
                <h1 className="ds-page-title">Gestione Candidati</h1>
                <p className="ds-page-subtitle">Anagrafica e profili CV</p>

                {/* Toolbar ricerca + filtri */}
                <div className="ds-toolbar">
                    <input
                        type="text"
                        className="ds-input ds-search"
                        placeholder="Cerca ovunque..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button onClick={() => setShowFilters(!showFilters)} className="ds-btn">
                        {showFilters ? 'Nascondi Filtri' : 'Filtri Avanzati'}
                    </button>
                </div>

                {showFilters && (
                    <div className="ds-panel" style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' }}>
                            {metadataFields.map(field => (
                                <div key={field}>
                                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--ds-txt-2)', marginBottom: '5px' }}>{field}</label>
                                    <input
                                        type="text"
                                        className="ds-input"
                                        value={filters[field] || ''}
                                        onChange={(e) => handleFilterChange(field, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <p style={{ color: 'var(--ds-txt-2)' }}>Caricamento...</p>
                ) : (
                    <>
                        <div className="ds-table-wrap">
                            <table className="ds-table">
                                <thead>
                                    <tr>
                                        <th>CVID</th>
                                        <th>Nominativo</th>
                                        <th>Email</th>
                                        <th>Anteprima Metadati</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {candidates.map(candidate => (
                                        <tr key={candidate.cvid}>
                                            <td style={{ maxWidth: '150px' }}>
                                                {String(candidate.cvid).split('###').map((part, index) => (
                                                    <div key={index} style={{ wordBreak: 'break-all' }}>{part.trim()}</div>
                                                ))}
                                            </td>
                                            <td>{candidate.metadata_json?.['Cognome e nome'] || '-'}</td>
                                            <td>{candidate.metadata_json?.['Indirizzo e-mail'] || '-'}</td>
                                            <td>
                                                <div style={{ fontSize: '12.5px', whiteSpace: 'normal' }}>
                                                    {candidate.metadata_json ? (
                                                        <>
                                                            <div style={{ marginBottom: '4px' }}>
                                                                <span className="ds-badge ds-badge-blue">Voto: {candidate.metadata_json['Voto CV'] || 'n/d'}</span>
                                                            </div>
                                                            <div><strong>Disp:</strong> {candidate.metadata_json['Disponibilità contrattuale']}</div>
                                                            <div><strong>Loc:</strong> {candidate.metadata_json['Regione/Provincia']}</div>
                                                        </>
                                                    ) : 'N/A'}
                                                    {candidate.cv_abstract && (
                                                        <div style={{ marginTop: '6px', borderTop: '0.5px solid var(--ds-border)', paddingTop: '6px', fontStyle: 'italic', color: 'var(--ds-txt-2)' }}>
                                                            <strong>Abstract:</strong> {candidate.cv_abstract}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {user?.role !== 'viewer' && (
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => handleEdit(candidate)} className="ds-btn ds-btn-primary">Modifica</button>
                                                        <button onClick={() => handleDelete(candidate.cvid)} className="ds-btn ds-btn-danger">Elimina</button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginazione */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '12px' }}>
                            <div style={{ fontSize: '13px', color: 'var(--ds-txt-2)' }}>
                                <label style={{ marginRight: '8px' }}>Righe per pagina:</label>
                                <select className="ds-select" style={{ width: 'auto', display: 'inline-block', height: '32px' }}
                                    value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '13px', color: 'var(--ds-txt-2)' }}>
                                    Pagina {page} di {Math.ceil(totalCount / limit) || 1} (Totale: {totalCount})
                                </span>
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="ds-btn">Precedente</button>
                                <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(totalCount / limit)} className="ds-btn">Successiva</button>
                            </div>
                        </div>
                    </>
                )}
            </main>

            {/* Modal Modifica Dinamico */}
            <CandidateEditModal
                show={showModal}
                onClose={() => setShowModal(false)}
                candidateId={editId}
                candidateData={editData}
                onSave={handleSave}
            />
        </div>
    );
};

export default MetadataManager;
